import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, Network as NetworkIcon, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const T = {
  de: {
    back: 'Zurück zur Startseite',
    title: 'Vermittler werden',
    intro: 'Werden Sie Teil unseres Vermittler-Netzwerks der Löwenherz Invest AG. Nach Prüfung Ihrer Angaben erhalten Sie einen persönlichen Referral-Link und einen individuell vereinbarten Provisions­satz auf vermittelte Investments.',
    sec: { contact: 'Kontaktdaten', address: 'Anschrift', bank: 'Bankverbindung (für Provisions-Auszahlung)', creds: 'Zugangsdaten' },
    f: {
      firstName: 'Vorname', lastName: 'Nachname', email: 'E-Mail', phone: 'Telefon',
      company: 'Firma (optional)',
      street: 'Strasse / Nr.', zip: 'PLZ', city: 'Ort', country: 'Land',
      holder: 'Kontoinhaber', holderPh: 'Vor- und Nachname / Firma',
      bank: 'Bank', bankPh: 'z. B. UBS, Raiffeisen', bic: 'BIC / SWIFT', iban: 'IBAN',
      pwd: 'Passwort (mind. 8 Zeichen)', pwdConfirm: 'Passwort bestätigen',
      notes: 'Anmerkungen (optional)', notesPh: 'z. B. Erfahrung im Finanzbereich, gewünschter Provisionsrahmen, ...'
    },
    terms: { prefix: 'Ich habe die ', agb: 'AGB', and: ' und die ', privacy: 'Datenschutzerklärung', suffix: ' gelesen und akzeptiere diese.' },
    notice: 'Hinweis: Nach Absenden wird Ihr Konto durch unsere Geschäftsleitung geprüft. Sie können sich erst einloggen, nachdem das Konto freigeschaltet und der individuelle Provisions­satz festgelegt wurde.',
    haveAccount: 'Bereits registriert? Zum Login',
    submit: 'Registrierung abschicken', submitting: 'Wird gesendet…',
    successTitle: 'Bewerbung eingegangen',
    successBody: 'Vielen Dank für Ihre Vermittler-Registrierung. Unser Team prüft Ihre Angaben und schaltet Ihr Konto in Kürze frei. Sie erhalten eine Bestätigung per E-Mail, sobald Sie sich einloggen können.',
    errPwd: 'Passwort muss mindestens 8 Zeichen lang sein',
    errPwdMatch: 'Passwörter stimmen nicht überein',
    errTerms: 'Bitte AGB und Risikohinweise akzeptieren',
    okToast: 'Registrierung erfolgreich',
  },
  en: {
    back: 'Back to home',
    title: 'Become a mediator',
    intro: 'Join the Löwenherz Invest AG mediator network. After review you receive a personal referral link and an individually agreed commission rate on mediated investments.',
    sec: { contact: 'Contact details', address: 'Address', bank: 'Bank account (for commission payout)', creds: 'Login credentials' },
    f: {
      firstName: 'First name', lastName: 'Last name', email: 'Email', phone: 'Phone',
      company: 'Company (optional)',
      street: 'Street / No.', zip: 'ZIP', city: 'City', country: 'Country',
      holder: 'Account holder', holderPh: 'First and last name / Company',
      bank: 'Bank', bankPh: 'e.g. UBS, Raiffeisen', bic: 'BIC / SWIFT', iban: 'IBAN',
      pwd: 'Password (min. 8 characters)', pwdConfirm: 'Confirm password',
      notes: 'Notes (optional)', notesPh: 'e.g. background in finance, desired commission range, ...'
    },
    terms: { prefix: 'I have read and accept the ', agb: 'Terms', and: ' and the ', privacy: 'Privacy Policy', suffix: '.' },
    notice: 'Note: After submission your account will be reviewed by our management. Login is possible only after activation and the individual commission rate has been set.',
    haveAccount: 'Already registered? Login',
    submit: 'Submit registration', submitting: 'Sending…',
    successTitle: 'Application received',
    successBody: 'Thank you for your mediator registration. Our team is reviewing your information and will activate your account shortly. You will receive an email confirmation once you can log in.',
    errPwd: 'Password must be at least 8 characters long',
    errPwdMatch: 'Passwords do not match',
    errTerms: 'Please accept terms and risk disclosures',
    okToast: 'Registration successful',
  },
  ru: {
    back: 'На главную',
    title: 'Стать посредником',
    intro: 'Станьте частью партнёрской сети Löwenherz Invest AG. После проверки заявки вы получите персональную реферальную ссылку и индивидуально согласованную комиссию по привлечённым инвестициям.',
    sec: { contact: 'Контактные данные', address: 'Адрес', bank: 'Банковские реквизиты (для выплаты комиссии)', creds: 'Учётные данные' },
    f: {
      firstName: 'Имя', lastName: 'Фамилия', email: 'Email', phone: 'Телефон',
      company: 'Компания (опц.)',
      street: 'Улица / №', zip: 'Индекс', city: 'Город', country: 'Страна',
      holder: 'Владелец счёта', holderPh: 'Имя и фамилия / Компания',
      bank: 'Банк', bankPh: 'например, UBS, Raiffeisen', bic: 'BIC / SWIFT', iban: 'IBAN',
      pwd: 'Пароль (мин. 8 символов)', pwdConfirm: 'Подтвердите пароль',
      notes: 'Комментарии (опц.)', notesPh: 'например, опыт в финансах, желаемый диапазон комиссии, ...'
    },
    terms: { prefix: 'Я прочитал(а) и принимаю ', agb: 'Условия', and: ' и ', privacy: 'Политику конфиденциальности', suffix: '.' },
    notice: 'Внимание: После отправки заявки ваш аккаунт будет проверен нашим руководством. Вход возможен только после активации и установления индивидуального процента комиссии.',
    haveAccount: 'Уже зарегистрированы? Войти',
    submit: 'Отправить заявку', submitting: 'Отправка…',
    successTitle: 'Заявка получена',
    successBody: 'Спасибо за вашу регистрацию в качестве посредника. Наша команда проверит ваши данные и активирует аккаунт в ближайшее время. Вы получите подтверждение по электронной почте, как только сможете войти в систему.',
    errPwd: 'Пароль должен быть длиной не менее 8 символов',
    errPwdMatch: 'Пароли не совпадают',
    errTerms: 'Примите Условия и предупреждения о рисках',
    okToast: 'Регистрация успешна',
  },
  vi: {
    back: 'Về trang chủ',
    title: 'Trở thành người môi giới',
    intro: 'Tham gia mạng lưới người môi giới của Löwenherz Invest AG. Sau khi xét duyệt, bạn sẽ nhận được liên kết giới thiệu cá nhân và mức hoa hồng được thoả thuận riêng cho các khoản đầu tư mà bạn giới thiệu.',
    sec: { contact: 'Thông tin liên hệ', address: 'Địa chỉ', bank: 'Tài khoản ngân hàng (để nhận hoa hồng)', creds: 'Thông tin đăng nhập' },
    f: {
      firstName: 'Tên', lastName: 'Họ', email: 'Email', phone: 'Điện thoại',
      company: 'Công ty (tuỳ chọn)',
      street: 'Đường / Số', zip: 'Mã bưu điện', city: 'Thành phố', country: 'Quốc gia',
      holder: 'Chủ tài khoản', holderPh: 'Tên đầy đủ / Tên công ty',
      bank: 'Ngân hàng', bankPh: 'Ví dụ: UBS, Raiffeisen', bic: 'BIC / SWIFT', iban: 'IBAN',
      pwd: 'Mật khẩu (ít nhất 8 ký tự)', pwdConfirm: 'Xác nhận mật khẩu',
      notes: 'Ghi chú (tuỳ chọn)', notesPh: 'Ví dụ: kinh nghiệm tài chính, khoản hoa hồng mong muốn, ...'
    },
    terms: { prefix: 'Tôi đã đọc và chấp nhận ', agb: 'Điều khoản', and: ' và ', privacy: 'Chính sách bảo mật', suffix: '.' },
    notice: 'Lưu ý: Sau khi gửi, tài khoản của bạn sẽ được ban quản lý kiểm tra. Bạn chỉ có thể đăng nhập sau khi tài khoản được kích hoạt và tỷ lệ hoa hồng được thiết lập.',
    haveAccount: 'Đã đăng ký? Đăng nhập',
    submit: 'Gửi đăng ký', submitting: 'Đang gửi…',
    successTitle: 'Đã nhận đơn đăng ký',
    successBody: 'Cảm ơn bạn đã đăng ký làm người môi giới. Đội ngũ chúng tôi sẽ kiểm tra thông tin và kích hoạt tài khoản của bạn trong thời gian sớm nhất. Bạn sẽ nhận được xác nhận qua email khi có thể đăng nhập.',
    errPwd: 'Mật khẩu phải có ít nhất 8 ký tự',
    errPwdMatch: 'Mật khẩu không khớp',
    errTerms: 'Vui lòng chấp nhận Điều khoản và cảnh báo rủi ro',
    okToast: 'Đăng ký thành công',
  },
};

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3 font-medium">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', required = false, placeholder, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="text-sm text-gray-300">
      {label} {required && <span className="text-red-400">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      required={required}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`med-reg-${name}`}
      className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
    />
  </label>
);

export const MediatorRegister = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = T[language] || T.de;
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '',
    company: '',
    street: '', zip: '', city: '', country: 'Schweiz',
    bankName: '', bankIBAN: '', bankBIC: '', bankAccountHolder: '',
    notes: '',
    terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 8) return setError(t.errPwd);
    if (form.password !== form.confirmPassword) return setError(t.errPwdMatch);
    if (!form.terms) return setError(t.errTerms);
    setSubmitting(true);
    try {
      const r = await fetch(apiBase() + '/api/mediators/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || t.submit);
      setDone(true);
      toast.success(t.okToast);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-black min-h-screen text-white">
        <Header />
        <main className="container mx-auto px-4 pt-32 pb-16 max-w-2xl" data-testid="page-mediator-register-success">
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-3">{t.successTitle}</h1>
            <p className="text-gray-200 leading-relaxed mb-6">{t.successBody}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C4A55A] hover:bg-[#D4B56A] text-black font-semibold"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.back}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white">
      <Header />
      <main className="container mx-auto px-4 pt-32 pb-16 max-w-3xl" data-testid="page-mediator-register">
        <Link
          to="/"
          className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] mb-5 text-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t.back}
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <NetworkIcon className="h-9 w-9 text-[#C4A55A]" />
          <h1 className="text-3xl md:text-4xl font-bold">{t.title}</h1>
        </div>
        <p className="text-gray-400 mb-8 leading-relaxed">{t.intro}</p>

        <form onSubmit={submit} className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8">
          <Section title={t.sec.contact}>
            <Field label={t.f.firstName} name="firstName" value={form.firstName} onChange={onChange} required />
            <Field label={t.f.lastName} name="lastName" value={form.lastName} onChange={onChange} required />
            <Field label={t.f.email} name="email" type="email" value={form.email} onChange={onChange} required />
            <Field label={t.f.phone} name="phone" value={form.phone} onChange={onChange} required placeholder="+41 76 …" />
            <Field label={t.f.company} name="company" value={form.company} onChange={onChange} full />
          </Section>

          <Section title={t.sec.address}>
            <Field label={t.f.street} name="street" value={form.street} onChange={onChange} required full />
            <Field label={t.f.zip} name="zip" value={form.zip} onChange={onChange} required />
            <Field label={t.f.city} name="city" value={form.city} onChange={onChange} required />
            <Field label={t.f.country} name="country" value={form.country} onChange={onChange} required full />
          </Section>

          <Section title={t.sec.bank}>
            <Field label={t.f.holder} name="bankAccountHolder" value={form.bankAccountHolder} onChange={onChange} placeholder={t.f.holderPh} full />
            <Field label={t.f.bank} name="bankName" value={form.bankName} onChange={onChange} placeholder={t.f.bankPh} />
            <Field label={t.f.bic} name="bankBIC" value={form.bankBIC} onChange={onChange} />
            <Field label={t.f.iban} name="bankIBAN" value={form.bankIBAN} onChange={onChange} placeholder="CH00 0000 0000 0000 0000 0" full />
          </Section>

          <Section title={t.sec.creds}>
            <Field label={t.f.pwd} name="password" type="password" value={form.password} onChange={onChange} required />
            <Field label={t.f.pwdConfirm} name="confirmPassword" type="password" value={form.confirmPassword} onChange={onChange} required />
          </Section>

          <label className="block mb-4">
            <span className="text-sm text-gray-300">{t.f.notes}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={3}
              className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none resize-none"
              placeholder={t.f.notesPh}
            />
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-200 mb-4">
            <input
              type="checkbox"
              name="terms"
              checked={form.terms}
              onChange={onChange}
              className="mt-1"
              data-testid="med-reg-terms"
            />
            <span>
              {t.terms.prefix}
              <Link to="/agb" target="_blank" className="text-[#C4A55A] hover:text-[#D4B56A] underline">{t.terms.agb}</Link>
              {t.terms.and}
              <Link to="/datenschutz" target="_blank" className="text-[#C4A55A] hover:text-[#D4B56A] underline">{t.terms.privacy}</Link>
              {t.terms.suffix}
            </span>
          </label>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm mb-4">
              {error}
            </div>
          )}

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-xs mb-5">
            <strong>{t.notice}</strong>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/vermittler/login')}
              className="text-sm text-[#C4A55A] hover:text-[#D4B56A]"
            >
              {t.haveAccount}
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="med-reg-submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};
